import React, { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import styled, { keyframes } from 'styled-components'
import { useApi } from '../../../hooks/useApi'
import Breadcrumbs from '../../structure/Breadcrumbs'

interface LogStream {
  creationTime: number
  arn: string
  firstEventTimestamp: number
  lastEventTimestamp: number
  lastIngestionTime: number
  logStreamName: string
  uploadSequenceToken: string
  storedBytes: number
}

const LogsStreams: React.FC = () => {
  const { callApi } = useApi()
  const navigate = useNavigate()
  const location = useLocation()
  const name = window.location.href.split('/groups?name=')
  const [logsGroups, setLogsStreams] = useState<LogStream[] | null>(null)
  const [next, setNext] = useState<string | null>(null)
  const [button, setButton] = useState({ disabled: false, message: 'See more' })

  const fetchData = async () => {
    try {
      const response = await callApi(`/admin/logs/${name[1]}`)
      if (response) {
        setLogsStreams(response?.logStreams)
        setNext(response?.nextToken || null)
      }
    } catch (error) {
      console.error('Error in fetchData:', error)
    }
  }

  const handleSeeMore = async () => {
    try {
      const response = await callApi(`/admin/logs/${name[1]}`, { nextToken: next })
      if (response?.logStreams && response?.logStreams.length > 0) {
        setLogsStreams(logsGroups ? logsGroups.concat(response.logStreams) : null)
        setNext(response?.nextToken || null)
      } else if (response?.logStreams?.length === 0) {
        setButton({ disabled: true, message: 'No more logs' })
      }
    } catch (error) {
      console.error('Error loading more logs:', error)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  useEffect(() => {
    const handleBack = (e: any) => {
      e.preventDefault()
      navigate('/dashboard/settings/logs')
    }
    window.addEventListener('popstate', handleBack)
    return () => window.removeEventListener('popstate', handleBack)
  }, [navigate])

  const handleRowClick = (id: string) => {
    navigate(`${location.pathname.split('logs/groups')[0]}logs/${id}/groups?name=${name[1]}`, { replace: true })
  }

  const paths = [
    { name: 'Logs', url: '/dashboard/settings/logs' },
    { name: name[1], url: '' },
  ]

  if (!logsGroups) return <LoaderBar />

  return (
    <Wrap>
      <Breadcrumbs paths={paths} />
      <TableWrap>
        <Table>
          <thead>
            <tr>
              <Th>Stream name</Th>
            </tr>
          </thead>
          <tbody>
            {logsGroups.map((item, index) => (
              <tr key={index} onClick={() => handleRowClick(item?.logStreamName)} style={{ cursor: 'pointer' }}>
                <Td><StreamName>{item?.logStreamName}</StreamName></Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrap>
      {next && (
        <SeeMoreBtn onClick={handleSeeMore} disabled={button.disabled}>
          {button.message}
        </SeeMoreBtn>
      )}
    </Wrap>
  )
}

export default LogsStreams

// ─── Styles ──────────────────────────────────────────────────────────────────

const Wrap = styled.div`display: flex; flex-direction: column; gap: 12px;`

const shimmer = keyframes`0% { transform: translateX(-100%); } 100% { transform: translateX(100%); }`
const LoaderBar = styled.div`
  height: 2px; background: rgba(255,255,255,0.06); border-radius: 2px; overflow: hidden; position: relative;
  &::after { content: ''; display: block; position: absolute; inset: 0; background: linear-gradient(90deg, transparent, rgba(111,92,230,0.5), transparent); animation: ${shimmer} 1.4s ease infinite; }
`

const TableWrap = styled.div`overflow-x: auto; border: 1px solid rgba(255,255,255,0.07); border-radius: 8px;`
const Table = styled.table`width: 100%; border-collapse: collapse; font-size: 13px;`
const Th = styled.th`padding: 10px 12px; text-align: left; color: #a1a1a1; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; border-bottom: 1px solid rgba(255,255,255,0.06);`
const Td = styled.td`padding: 8px 10px; color: #a0a0a0; font-size: 13px; vertical-align: middle; border-bottom: 1px solid rgba(255,255,255,0.04); tr:last-child & { border-bottom: none; }`
const StreamName = styled.span`font-family: 'SF Mono','Fira Code',monospace; font-size: 12px; color: #a89aff;`

const SeeMoreBtn = styled.button`
  height: 28px; padding: 0 12px; background: rgba(111,92,230,0.1); border: 1px solid rgba(111,92,230,0.3);
  border-radius: 5px; color: #a89aff; font-size: 12px; font-weight: 500; cursor: pointer; align-self: flex-start;
  transition: background 0.15s; &:hover:not(:disabled) { background: rgba(111,92,230,0.2); border-color: rgba(111,92,230,0.5); }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`
